import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ClipboardCheck, Save, Calendar, Filter, X } from 'lucide-react';
import ChildAvatar from '@/components/ChildAvatar';

const Attendance = () => {
  const [children, setChildren] = useState([]);
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});
  const [selectedOrigins, setSelectedOrigins] = useState(['livre_demanda', 'gov_celso_ramos', 'biguacu']);
  const [selectedShifts, setSelectedShifts] = useState(['Matutino', 'Vespertino']);
  const [selectedDays, setSelectedDays] = useState(['ter_qui', 'qua_sex', 'seg_sex']);
  const [selectedAges, setSelectedAges] = useState(['6-8', '9-11', '12-15']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const origins = [
    { value: 'livre_demanda', label: 'Livre Demanda' },
    { value: 'gov_celso_ramos', label: 'Governador Celso Ramos' },
    { value: 'biguacu', label: 'Biguaçu' }
  ];

  const shifts = [
    { value: 'Matutino', label: 'Manhã' },
    { value: 'Vespertino', label: 'Tarde' }
  ];

  const dayPatterns = [
    { value: 'ter_qui', label: 'Ter e Qui' },
    { value: 'qua_sex', label: 'Qua e Sex' },
    { value: 'seg_sex', label: 'Seg a Sex' }
  ];

  const ageRanges = [
    { value: '6-8', label: '6 a 8 anos' },
    { value: '9-11', label: '9 a 11 anos' },
    { value: '12-15', label: '12 a 15 anos' }
  ];

  // Mapear dia da semana para padrões de dias
  const getDayPatternsForDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const dow = date.getDay(); // 0=dom, 1=seg, 2=ter, 3=qua, 4=qui, 5=sex, 6=sab
    const patterns = [];
    // seg_sex frequenta todos os dias úteis
    if (dow >= 1 && dow <= 5) patterns.push('seg_sex');
    // ter_qui frequenta terça e quinta
    if (dow === 2 || dow === 4) patterns.push('ter_qui');
    // qua_sex frequenta quarta e sexta
    if (dow === 3 || dow === 5) patterns.push('qua_sex');
    return patterns;
  };

  const dayOfWeekLabel = (dateStr) => {
    const labels = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const date = new Date(dateStr + 'T12:00:00');
    return labels[date.getDay()];
  };

  useEffect(() => {
    fetchChildren();
  }, []);

  // Quando a data muda, auto-selecionar os padrões de dias corretos
  useEffect(() => {
    const validPatterns = getDayPatternsForDate(attendanceDate);
    setSelectedDays(validPatterns);
  }, [attendanceDate]);

  useEffect(() => {
    if (children.length > 0) {
      loadAttendance();
    }
  }, [attendanceDate, children]);

  useEffect(() => {
    // Filter children by all selected criteria
    let filtered = children.filter(child => {
      // Filtro por origem
      if (!selectedOrigins.includes(child.enrollment_origin)) {
        return false;
      }

      // Filtro por turno
      if (!selectedShifts.includes(child.shift)) {
        return false;
      }

      // Filtro por dias
      const childDays = child.days_of_week || [];
      const matchesDays = selectedDays.some(pattern => {
        if (pattern === 'ter_qui') {
          return childDays.includes('terca') && childDays.includes('quinta');
        } else if (pattern === 'qua_sex') {
          return childDays.includes('quarta') && childDays.includes('sexta');
        } else if (pattern === 'seg_sex') {
          return childDays.includes('segunda') && 
                 childDays.includes('terca') && 
                 childDays.includes('quarta') && 
                 childDays.includes('quinta') && 
                 childDays.includes('sexta');
        }
        return false;
      });
      if (!matchesDays) {
        return false;
      }

      // Filtro por idade
      const age = child.age || 0;
      const matchesAge = selectedAges.some(range => {
        if (range === '6-8') return age >= 6 && age <= 8;
        if (range === '9-11') return age >= 9 && age <= 11;
        if (range === '12-15') return age >= 12 && age <= 15;
        return false;
      });
      if (!matchesAge) {
        return false;
      }

      return true;
    });
    
    // Ordenar: primeiro por origem, depois alfabeticamente por nome
    filtered.sort((a, b) => {
      // Ordem das origens
      const originOrder = {
        'livre_demanda': 1,
        'gov_celso_ramos': 2,
        'biguacu': 3
      };
      
      const originCompare = (originOrder[a.enrollment_origin] || 999) - (originOrder[b.enrollment_origin] || 999);
      
      // Se mesma origem, ordenar por nome
      if (originCompare === 0) {
        return (a.name || '').localeCompare(b.name || '');
      }
      
      return originCompare;
    });
    
    setFilteredChildren(filtered);
  }, [selectedOrigins, selectedShifts, selectedDays, selectedAges, children]);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/children`, {
        params: { active_only: true }
      });
      setChildren(response.data);
      setFilteredChildren(response.data);
    } catch (error) {
      toast.error('Erro ao carregar crianças');
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async () => {
    try {
      const response = await axios.get(`${API}/attendance`, {
        params: { date: attendanceDate }
      });
      
      const data = {};
      response.data.forEach(att => {
        data[att.child_id] = {
          present: att.present,
          justified: att.justified || false,
          observations: att.observations || ''
        };
      });
      
      // Initialize missing children with null (not registered)
      children.forEach(child => {
        if (!data[child.id]) {
          data[child.id] = { present: null, justified: false, observations: '' };
        }
      });
      
      setAttendanceData(data);
    } catch (error) {
      toast.error('Erro ao carregar presenças');
    }
  };

  const togglePresence = (childId) => {
    setAttendanceData(prev => {
      const current = prev[childId];
      let newState = {};
      
      // Ciclo: vazio → presente → ausente → justificado → vazio
      if (!current || (current.present === null || current.present === undefined)) {
        // Estado 1: vazio → presente (verde)
        newState = { present: true, justified: false, observations: current?.observations || '' };
      } else if (current.present === true && !current.justified) {
        // Estado 2: presente → ausente (vermelho)
        newState = { present: false, justified: false, observations: current?.observations || '' };
      } else if (current.present === false && !current.justified) {
        // Estado 3: ausente → justificado (laranja)
        newState = { present: false, justified: true, observations: current?.observations || '' };
      } else {
        // Estado 4: justificado → vazio (null)
        newState = { present: null, justified: false, observations: current?.observations || '' };
      }
      
      return {
        ...prev,
        [childId]: newState
      };
    });
  };

  const updateObservation = (childId, observation) => {
    setAttendanceData(prev => ({
      ...prev,
      [childId]: {
        ...prev[childId],
        observations: observation
      }
    }));
  };

  const toggleOriginFilter = (origin) => {
    setSelectedOrigins(prev => 
      prev.includes(origin)
        ? prev.filter(o => o !== origin)
        : [...prev, origin]
    );
  };

  const selectAllOrigins = () => {
    setSelectedOrigins(['livre_demanda', 'gov_celso_ramos', 'biguacu']);
  };

  const toggleShiftFilter = (shift) => {
    setSelectedShifts(prev => 
      prev.includes(shift)
        ? prev.filter(s => s !== shift)
        : [...prev, shift]
    );
  };

  const selectAllShifts = () => {
    setSelectedShifts(['Matutino', 'Vespertino']);
  };

  const toggleDaysFilter = (days) => {
    const validPatterns = getDayPatternsForDate(attendanceDate);
    if (!validPatterns.includes(days)) return;
    setSelectedDays(prev => 
      prev.includes(days)
        ? prev.filter(d => d !== days)
        : [...prev, days]
    );
  };

  const selectAllDays = () => {
    setSelectedDays(getDayPatternsForDate(attendanceDate));
  };

  const toggleAgeFilter = (age) => {
    setSelectedAges(prev => 
      prev.includes(age)
        ? prev.filter(a => a !== age)
        : [...prev, age]
    );
  };

  const selectAllAges = () => {
    setSelectedAges(['6-8', '9-11', '12-15']);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only save attendance for filtered children
      const attendances = filteredChildren.map(child => {
        const childData = attendanceData[child.id];
        return {
          child_id: child.id,
          present: childData?.present === null ? null : (childData?.present || false),
          justified: childData?.justified || false,
          observations: childData?.observations || ''
        };
      });

      await axios.post(`${API}/attendance/bulk`, {
        date: attendanceDate,
        attendances
      });
      
      toast.success('Presenças salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar presenças');
    } finally {
      setSaving(false);
    }
  };

  const presentCount = filteredChildren.filter(child => 
    attendanceData[child.id]?.present === true && !attendanceData[child.id]?.justified
  ).length;
  const absentCount = filteredChildren.filter(child => 
    attendanceData[child.id]?.present === false && !attendanceData[child.id]?.justified
  ).length;
  const justifiedCount = filteredChildren.filter(child => 
    attendanceData[child.id]?.justified === true
  ).length;
  const totalCount = filteredChildren.length;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Chamada Diária</h1>
        <p className="text-slate-600 mt-1">Registre a presença das crianças</p>
      </div>

      {/* Date Selector and Stats */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="w-full sm:w-auto">
            <Label htmlFor="attendance-date">Data da Chamada</Label>
            <div className="flex items-center gap-2 mt-2">
              <Calendar className="h-5 w-5 text-slate-400" />
              <Input
                id="attendance-date"
                type="date"
                data-testid="attendance-date-input"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                className="w-auto"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="text-center">
              <p className="text-sm text-slate-600">Presentes</p>
              <p className="text-3xl font-bold text-green-600" data-testid="present-count">{presentCount}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-slate-600">Ausentes</p>
              <p className="text-3xl font-bold text-red-600">{absentCount}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-slate-600">Justificados</p>
              <p className="text-3xl font-bold text-orange-600">{justifiedCount}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-slate-600">Total</p>
              <p className="text-3xl font-bold text-slate-800">{totalCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Filtros de Chamada</h3>
        </div>

        {/* Origem */}
        <div className="mb-4">
          <Label className="text-sm font-medium text-slate-700 mb-2">Origem</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllOrigins}
              data-testid="select-all-origins"
              className="text-xs"
            >
              Todas
            </Button>
            {origins.map(origin => (
              <button
                key={origin.value}
                type="button"
                data-testid={`filter-${origin.value}`}
                onClick={() => toggleOriginFilter(origin.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedOrigins.includes(origin.value)
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {origin.label}
              </button>
            ))}
          </div>
        </div>

        {/* Turno */}
        <div className="mb-4">
          <Label className="text-sm font-medium text-slate-700 mb-2">Turno</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllShifts}
              data-testid="select-all-shifts"
              className="text-xs"
            >
              Todos
            </Button>
            {shifts.map(shift => (
              <button
                key={shift.value}
                type="button"
                data-testid={`filter-shift-${shift.value}`}
                onClick={() => toggleShiftFilter(shift.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedShifts.includes(shift.value)
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {shift.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dias */}
        <div className="mb-4">
          <Label className="text-sm font-medium text-slate-700 mb-2">
            Dias de Participação
            <span className="text-xs font-normal text-indigo-600 ml-2">
              ({dayOfWeekLabel(attendanceDate)})
            </span>
          </Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {dayPatterns.map(day => {
              const validPatterns = getDayPatternsForDate(attendanceDate);
              const isValidForDate = validPatterns.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  data-testid={`filter-days-${day.value}`}
                  onClick={() => isValidForDate && toggleDaysFilter(day.value)}
                  disabled={!isValidForDate}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    !isValidForDate
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                      : selectedDays.includes(day.value)
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Idade */}
        <div className="mb-4">
          <Label className="text-sm font-medium text-slate-700 mb-2">Faixa Etária</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllAges}
              data-testid="select-all-ages"
              className="text-xs"
            >
              Todas
            </Button>
            {ageRanges.map(age => (
              <button
                key={age.value}
                type="button"
                data-testid={`filter-age-${age.value}`}
                onClick={() => toggleAgeFilter(age.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedAges.includes(age.value)
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {age.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t border-slate-200">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{totalCount}</span> crianças com os filtros selecionados
          </p>
        </div>
      </div>

      {/* Attendance List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="space-y-4" data-testid="attendance-list">
            {(() => {
              let currentOrigin = null;
              const originLabels = {
                'livre_demanda': 'Livre Demanda',
                'gov_celso_ramos': 'Governador Celso Ramos',
                'biguacu': 'Biguaçu'
              };
              
              return filteredChildren.map((child, index) => {
                const showOriginHeader = child.enrollment_origin !== currentOrigin;
                currentOrigin = child.enrollment_origin;
                
                return (
                  <div key={child.id}>
                    {/* Cabeçalho da origem */}
                    {showOriginHeader && (
                      <div className="bg-blue-50 border-l-4 border-blue-500 px-4 py-2 mb-3 rounded">
                        <h3 className="font-bold text-blue-800 text-sm">
                          📍 {originLabels[child.enrollment_origin] || child.enrollment_origin}
                        </h3>
                      </div>
                    )}
                    
                    {/* Card da criança */}
                    <div
                      className="bg-white rounded-xl shadow-md p-4 border border-slate-100 transition-all hover:shadow-lg"
                    >
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <button
                      type="button"
                      data-testid={`attendance-toggle-${child.id}`}
                      onClick={() => togglePresence(child.id)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                        attendanceData[child.id]?.present === true && !attendanceData[child.id]?.justified
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : attendanceData[child.id]?.present === false && !attendanceData[child.id]?.justified
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : attendanceData[child.id]?.justified === true
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                      }`}
                      title={
                        attendanceData[child.id]?.present === true && !attendanceData[child.id]?.justified
                          ? 'Presente (clique para marcar Ausente)'
                          : attendanceData[child.id]?.present === false && !attendanceData[child.id]?.justified
                          ? 'Ausente (clique para marcar Justificado)'
                          : attendanceData[child.id]?.justified === true
                          ? 'Falta Justificada (clique para limpar)'
                          : 'Não registrado (clique para marcar Presente)'
                      }
                    >
                      {attendanceData[child.id]?.present === true && !attendanceData[child.id]?.justified
                        ? '✓'
                        : attendanceData[child.id]?.present === false && !attendanceData[child.id]?.justified
                        ? '×'
                        : attendanceData[child.id]?.justified === true
                        ? 'J'
                        : '—'}
                    </button>
                    
                    <div>
                      <ChildAvatar 
                        photo={child.photo_url} 
                        name={child.name} 
                        size="md"
                        zoomable={true}
                      />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800">{child.name}</h3>
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <span>{child.age} anos - {child.gender?.toUpperCase() === 'MASCULINO' ? 'M' : 'F'}</span>
                        <span>•</span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded">
                          {child.enrollment_origin === 'livre_demanda' ? 'Livre Demanda' :
                           child.enrollment_origin === 'gov_celso_ramos' ? 'Governador Celso Ramos' : 'Biguaçu'}
                        </span>
                        <span>•</span>
                        <span>{child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-full sm:w-64">
                    <Input
                      placeholder="Observações..."
                      data-testid={`attendance-observation-${child.id}`}
                      value={attendanceData[child.id]?.observations || ''}
                      onChange={(e) => updateObservation(child.id, e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        });
      })()}
          </div>

          {/* Save Button */}
          {filteredChildren.length > 0 && (
            <div className="sticky bottom-4 flex justify-center">
              <Button
                size="lg"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-attendance-button"
                className="shadow-lg"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? 'Salvando...' : 'Salvar Presenças'}
              </Button>
            </div>
          )}
        </>
      )}

      {!loading && filteredChildren.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <ClipboardCheck className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhuma criança encontrada com os filtros selecionados</p>
        </div>
      )}
    </div>
  );
};

export default Attendance;